import React from 'react';
import { motion } from 'framer-motion';
import { Target, TrendingUp, AlertCircle, Eye, BarChart3 } from 'lucide-react';

const AIScoutWorkspace: React.FC = () => {
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="p-3 border-b border-slate-700/50 flex-shrink-0">
        <h3 className="text-lg font-medium violett-gradient-text text-drop-shadow">AI Scout</h3>
        <p className="text-sm text-slate-400 mt-1">Competitive Intelligence & Market Analysis</p>
      </div>
      
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          <motion.div 
            className="glass-dark rounded-xl p-6 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Target className="w-6 h-6 text-blue-400" />
              <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Market Intelligence</h4>
            </div>
            <div className="space-y-3">
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Competitor Analysis</span>
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Active</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">Monitoring 12 key competitors across 5 market segments</p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div className="bg-gradient-to-r from-green-400 to-emerald-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                  </div>
                  <span className="text-xs text-slate-400">85%</span>
                </div>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Trend Analysis</span>
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">Processing</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">Real-time market trend detection and forecasting</p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div className="bg-gradient-to-r from-blue-400 to-purple-500 h-2 rounded-full" style={{ width: '67%' }}></div>
                  </div>
                  <span className="text-xs text-slate-400">67%</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-dark rounded-xl p-6 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-6 h-6 text-purple-400" />
              <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Active Monitoring</h4>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="font-medium">Social Sentiment</span>
                </div>
                <span className="text-sm font-medium text-green-400">Positive</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <span className="font-medium">News Coverage</span>
                </div>
                <span className="text-sm font-medium text-yellow-400">Moderate</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span className="font-medium">Patent Filings</span>
                </div>
                <span className="text-sm font-medium text-blue-400">3 new</span>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-dark rounded-xl p-6 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-6 h-6 text-cyan-400" />
              <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Market Opportunities</h4>
            </div>
            <div className="space-y-3">
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">AI/ML Integration</span>
                  <span className="text-xs text-slate-400">High Priority</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">Growing demand for AI-powered solutions in enterprise</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Cloud Migration</span>
                  <span className="text-xs text-slate-400">Medium Priority</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">Continued shift to cloud-first architectures</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Security Focus</span>
                  <span className="text-xs text-slate-400">Critical</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">Increased emphasis on zero-trust security models</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-dark rounded-xl p-6 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <BarChart3 className="w-6 h-6 text-green-400" />
              <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Intelligence Metrics</h4>
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">Data Coverage</span>
                  <span className="text-sm font-medium text-green-400">94%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div className="bg-gradient-to-r from-green-400 to-emerald-500 h-2 rounded-full" style={{ width: '94%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">Analysis Accuracy</span>
                  <span className="text-sm font-medium text-blue-400">91%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div className="bg-gradient-to-r from-blue-400 to-purple-500 h-2 rounded-full" style={{ width: '91%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">Response Time</span>
                  <span className="text-sm font-medium text-cyan-400">1.2s avg</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div className="bg-gradient-to-r from-cyan-400 to-blue-500 h-2 rounded-full" style={{ width: '88%' }}></div>
                </div>
              </div>
            </div>
          </motion.div>

        </div>

        <motion.div 
          className="mt-6 glass-dark rounded-xl p-6 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-6 h-6 text-orange-400" />
            <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Recent Intelligence</h4>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
              <div className="w-2 h-2 bg-red-400 rounded-full"></div>
              <span className="text-sm">Competitor launched new AI feature - requires immediate analysis</span>
              <span className="text-xs text-slate-400 ml-auto">3 min ago</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span className="text-sm">Market sentiment shift detected in enterprise segment</span>
              <span className="text-xs text-slate-400 ml-auto">12 min ago</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-sm">New partnership opportunity identified through patent analysis</span>
              <span className="text-xs text-slate-400 ml-auto">28 min ago</span>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default AIScoutWorkspace;
